import React, { useState, useEffect } from 'react';
import './StockList.css';
import axios from 'axios';

const StockList = () => {
  // 生成年份選項 (從當前年份往前推5年)
  const currentYear = new Date().getFullYear();
  
  // 年份選擇狀態 - 預設為當年度
  const [selectedYear, setSelectedYear] = useState(currentYear.toString());
  
  // 搜尋輸入狀態
  const [searchInput, setSearchInput] = useState('');
  
  // 股票資料狀態
  const [stocks, setStocks] = useState([]);
  
  // 新增: 儲存完整的股票資料
  const [fullStockList, setFullStockList] = useState([]);
  
  // 載入狀態
  const [loading, setLoading] = useState(false);
  
  // 年份選項列表 - 只包含今年和去年
  const years = [currentYear.toString(), (currentYear - 1).toString()];
  
  // 修改排序狀態的初始值
  const [sortConfig, setSortConfig] = useState({
    key: 'date_meeting',  // 預設排序欄位
    direction: 'descending'  // 預設降序排序
  });

  // 格式化日期函數 - 將ISO日期格式轉換為YYYY-MM-DD
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // 如果日期無效，返回原始字串
      return date.toISOString().split('T')[0]; // 只取日期部分 (YYYY-MM-DD)
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString; // 發生錯誤時返回原始字串
    }
  };

  // 獲取股票資料的函數
  const fetchStockData = async (year) => {
    setLoading(true);
    try {
      let url = `${process.env.REACT_APP_API_BASE_URL}/api/stocklist/search?year=${year-1911}`;
      
      const response = await axios.get(url);
      
      let stocksData = [];
      
      // 確保 stocks 始終是一個陣列
      if (Array.isArray(response.data)) {
        stocksData = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // 如果 response.data 是一個物件，檢查是否有 data 屬性
        if (Array.isArray(response.data.data)) {
          stocksData = response.data.data;
        } else {
          // 如果沒有 data 屬性或 data 不是陣列，將物件轉換為陣列
          console.warn('API response is not an array, attempting to convert:', response.data);
          stocksData = Object.values(response.data).filter(item => item && typeof item === 'object');
        }
      } else {
        // 如果 response.data 不是陣列也不是物件，設置為空陣列
        console.error('API response is not in expected format:', response.data);
        stocksData = [];
      }
      
      // 設置完整的股票列表和當前顯示的股票列表
      setFullStockList(stocksData);
      setStocks(stocksData);
    } catch (error) {
      console.error('Error fetching stock data:', error);
      // 如果API請求失敗，使用空陣列
      setFullStockList([]);
      setStocks([]);
    } finally {
      setLoading(false);
    }
  };

  // 從本地存儲的完整列表中搜尋
  const searchFromLocalData = (searchTerm) => {
    if (!searchTerm.trim()) {
      // 如果搜尋詞為空，顯示所有股票
      setStocks(fullStockList);
      return;
    }
    
    // 將搜尋詞轉為小寫以進行不區分大小寫的搜尋
    const term = searchTerm.toLowerCase();
    
    // 從完整列表中過濾符合搜尋條件的股票
    const filteredStocks = fullStockList.filter(stock => 
      (stock.stock_code && stock.stock_code.toString().toLowerCase().includes(term)) || 
      (stock.stock_name && stock.stock_name.toLowerCase().includes(term)) ||
      (stock.gift_item && stock.gift_item.toLowerCase().includes(term))
    );
    
    setStocks(filteredStocks);
  };

  // 合併兩個useEffect，只在組件掛載或年份變更時獲取資料
  useEffect(() => {
    // 避免重複API調用的標誌
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await fetchStockData(selectedYear);
      }
    };
    
    loadData();
    
    // 清理函數
    return () => {
      isMounted = false;
    };
  }, [selectedYear]); // 只在selectedYear變更時重新獲取資料

  // 排序處理函數
  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // 排序資料
  const sortedStocks = React.useMemo(() => {
    // 確保 stocks 是一個陣列
    if (!Array.isArray(stocks)) {
      console.error('stocks is not an array:', stocks);
      return [];
    }
    
    return [...stocks].sort((a, b) => {
      if (!sortConfig.key) return 0;
      
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }, [stocks, sortConfig]);

  // 獲取排序指示器
  const getSortIndicator = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
    }
    return '';
  };

  // 處理年份選擇變更
  const handleYearChange = (e) => {
    setSelectedYear(e.target.value);
  };

  // 處理搜尋輸入變更
  const handleSearchInputChange = (e) => {
    const searchTerm = e.target.value;
    setSearchInput(searchTerm);
    searchFromLocalData(searchTerm);
  };

  return (
    <div className="stocklist-container">
      
      <div className="search-section">
        <select 
          className="year-dropdown"
          value={selectedYear}
          onChange={handleYearChange}
          aria-label="選擇年份"
        >
          {years.map(year => (
            <option key={year} value={year}>
              {`${year-1911}年`}
            </option>
          ))}
        </select>
        <input 
          type="text" 
          placeholder="搜尋股票代號或名稱" 
          className="stock-search-input"
          value={searchInput}
          onChange={handleSearchInputChange}
          aria-label="搜尋股票"
        />
      </div>

      {loading ? (
        <div className="loading">載入中...</div>
      ) : (
        <div className="stock-table">
          {stocks.length > 0 ? (
            <>
              <div className="table-header">
                <div className="header-cell" onClick={() => handleSort('stock_code')}>
                  股號{getSortIndicator('stock_code')}
                </div>
                <div className="header-cell" onClick={() => handleSort('stock_name')}>
                  股名{getSortIndicator('stock_name')}
                </div>
                <div className="header-cell" onClick={() => handleSort('gift_item')}>
                  紀念品{getSortIndicator('gift_item')}
                </div>
                <div className="header-cell" onClick={() => handleSort('date_meeting')}>
                  開會日期{getSortIndicator('date_meeting')}
                </div>
                <div className="header-cell" onClick={() => handleSort('date_finalbuy')}>
                  最後買進日{getSortIndicator('date_finalbuy')}
                </div>
                <div className="header-cell" onClick={() => handleSort('stock_agent')}>
                  股代{getSortIndicator('stock_agent')}
                </div>
              </div>
              
              {sortedStocks.map(stock => (
                <div key={stock.id} className="table-row">
                  <div className="cell">{stock.stock_code}</div>
                  <div className="cell">{stock.stock_name}</div>
                  <div className="cell">{stock.gift_item}</div>
                  <div className="cell">{formatDate(stock.date_meeting)}</div>
                  <div className="cell">{formatDate(stock.date_finalbuy)}</div>
                  <div className="cell">{stock.stock_agent}</div>
                </div>
              ))}
            </>
          ) : (
            <div className="no-data">沒有符合條件的股票資料</div>
          )}
        </div>
      )}
    </div>
  );
};

export default StockList; 